import { Navigate, Outlet, useLocation } from 'react-router'
import { useSidebarStore } from '@/stores/sidebarStore'
import { useBrand } from '@/context/brandContext'
import { Sidebar } from '@/components/sidebar/sidebar'
import { Header } from '@/components/ui/header'
import { usePlanPersonProperties } from '@/lib/analytics/usePlanPersonProperties'

// Paths reachable while a brand is still mid-onboarding: the onboarding flow
// itself (/company-setup, the moodboard canvas) plus account/brand/billing
// management. Everything else redirects to /company-setup until the brand is
// onboarded. See docs/design/onboarding-moodboard.md.
const ONBOARDING_ALLOWED_PREFIXES = [
  '/company-setup',
  '/moodboards',
  '/account',
  '/settings',
  '/brand',
  '/oauth',
  '/pricing',
  '/success',
  '/cancel',
]

export function Layout() {
  const { isOpen } = useSidebarStore()
  const { currentBrand } = useBrand()
  const location = useLocation()
  usePlanPersonProperties()

  // Onboarding gate. Keyed off brand.onboardedAt (set when the user finishes the
  // moodboard flow), replacing the old implicit "a persona exists" signal. Only
  // acts once the brand has loaded, so we never bounce on a transient null.
  const needsOnboarding = currentBrand != null && currentBrand.onboardedAt == null
  const onAllowedPath = ONBOARDING_ALLOWED_PREFIXES.some((p) => location.pathname.startsWith(p))
  if (needsOnboarding && !onAllowedPath) {
    return <Navigate to="/company-setup" replace />
  }

  // The onboarding moodboard canvas (/moodboards/:id?onboarding=1) is part of the
  // onboarding flow — hide the sidebar/header there too so nothing leads the user
  // out. The flow's own surfaces (/company-setup, /company-setup/finish) are
  // already fullscreen.
  const onboardingCanvas =
    location.pathname.startsWith('/moodboards/') &&
    new URLSearchParams(location.search).get('onboarding') === '1'
  const isFullscreen =
    location.pathname.startsWith('/onboarding') ||
    location.pathname.startsWith('/company-setup') ||
    onboardingCanvas

  const style = isFullscreen
    ? 'flex-1 min-h-screen'
    : location.pathname.startsWith('/workspace')
      ? 'flex-1 min-h-screen'
      : `flex-1 ${isOpen ? 'md:ml-64' : 'ml-0'} transition-all duration-300 ease-in-out min-h-screen`

  return (
    <div className="flex">
      {!isFullscreen && <Sidebar />}
      <main className={style}>
        {!(location.pathname.startsWith('/workspace') || isFullscreen) && <Header />}
        <Outlet />
      </main>
    </div>
  )
}
